import { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle2, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const serviceOptions = [
  "Tax Preparation & Filing",
  "Tax Planning & Strategy",
  "Bookkeeping & Accounting",
  "IRS Issue or Tax Problem",
  "New Business Setup",
  "I'm not sure yet — I just want to talk"
];

const validatePhone = (phone) => {
  if (!phone) return true;
  const digits = phone.replace(/\D/g, '');
  return digits.length >= 10 && digits.length <= 15;
};

const formatPhone = (value) => {
  const digits = value.replace(/\D/g, '');
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

const validateEmail = (email) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

export const LeadCaptureForm = () => {
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    business_name: '',
    service_interest: '',
    notes: ''
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (field, value) => {
    if (field === 'phone') {
      value = formatPhone(value);
    }
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = 'First name is required';
    if (!form.last_name.trim()) e.last_name = 'Last name is required';
    if (!form.email.trim()) e.email = 'Email is required';
    else if (!validateEmail(form.email)) e.email = 'Please enter a valid email';
    if (form.phone && !validatePhone(form.phone)) e.phone = 'Please enter a valid phone number';
    if (!form.service_interest) e.service_interest = 'Please select a service';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      const params = new URLSearchParams(window.location.search);
      const assessment = window.__kgob_assessment || { completed: false };

      const payload = {
        source: 'welcome_page',
        timestamp: new Date().toISOString(),
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone ? form.phone.replace(/\D/g, '') : null,
        business_name: form.business_name.trim() || null,
        service_interest: form.service_interest,
        notes: form.notes.trim() || null,
        assessment_data: assessment,
        utm_source: params.get('utm_source') || null,
        utm_medium: params.get('utm_medium') || null,
        utm_campaign: params.get('utm_campaign') || null
      };

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);

      const res = await fetch(`${API_URL}/api/v1/welcome/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        // Store lead_id globally for CTA tracking
        if (data.lead_id) {
          window.__kgob_lead_id = data.lead_id;
          window.__kgob_lead_email = payload.email;
        }
        setSubmitted(true);
      } else {
        const data = await res.json().catch(() => ({}));
        // Handle Pydantic validation errors (array format) and string errors
        let errorMsg = 'Something went wrong. Please try again.';
        if (data.detail) {
          if (Array.isArray(data.detail)) {
            // Pydantic validation errors come as array of objects
            errorMsg = (data.detail || []).map(e => e.msg || 'Validation error').join(', ');
          } else if (typeof data.detail === 'string') {
            errorMsg = data.detail;
          }
        }
        setErrors({ submit: errorMsg });
      }
    } catch (err) {
      const msg = err?.name === 'AbortError'
        ? 'Request timed out. Please try again.'
        : 'Network error. Please check your connection and try again.';
      setErrors({ submit: msg });
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section id="contact" className="relative bg-white py-20 lg:py-28" data-testid="welcome-lead-form">
        <div className="mx-auto max-w-xl px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[#F7F6F2] rounded-2xl p-8 sm:p-10"
            data-testid="lead-form-success"
          >
            <CheckCircle2 className="h-12 w-12 text-[#1FA3A3] mx-auto mb-4" />
            <h3 className="font-['Playfair_Display'] text-2xl font-semibold text-[#0B2240] mb-3">
              Thank you, {form.first_name}.
            </h3>
            <p className="text-[#4C5A70] leading-relaxed">
              One of our advisors will reach out within one business day to schedule your free strategy session.
              In the meantime, feel free to call us directly at{' '}
              <a data-testid="lead-capture-form-a-1" href="tel:7045993355" className="text-[#D4AF37] font-medium hover:underline">
                (704) 599-3355
              </a>.
            </p>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section id="contact" className="relative bg-white py-20 lg:py-28" data-testid="welcome-lead-form">
      <div className="absolute inset-0 bg-[radial-gradient(800px_400px_at_20%_80%,rgba(212,175,55,0.04),transparent)]" />

      <div className="relative mx-auto max-w-xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-10"
        >
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#0B2240]">
            Let's start a conversation.
          </h2>
          <p className="mt-4 text-base sm:text-lg text-[#4C5A70]">
            No commitment. No hard sell. Just a straightforward discussion about whether we're the right fit for your situation.
          </p>
        </motion.div>

        <form onSubmit={handleSubmit} className="space-y-5" data-testid="welcome-lead-capture-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="first_name" className="block text-sm font-medium text-[#0B2240] mb-1.5">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                id="first_name"
                type="text"
                value={form.first_name}
                onChange={(e) => handleChange('first_name', e.target.value)}
                placeholder="First Name"
                className={`w-full px-4 py-3 rounded-xl border text-[16px] bg-[#F7F6F2] text-[#0B2240] placeholder:text-[#4C5A70]/50 focus:outline-none focus:ring-2 focus:ring-[#1FA3A3] transition-all ${
                  errors.first_name ? 'border-red-400' : 'border-gray-200'
                }`}
                data-testid="lead-form-first-name"
              />
              {errors.first_name && <p className="text-red-500 text-xs mt-1">{errors.first_name}</p>}
            </div>
            <div>
              <label htmlFor="last_name" className="block text-sm font-medium text-[#0B2240] mb-1.5">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                id="last_name"
                type="text"
                value={form.last_name}
                onChange={(e) => handleChange('last_name', e.target.value)}
                placeholder="Last Name"
                className={`w-full px-4 py-3 rounded-xl border text-[16px] bg-[#F7F6F2] text-[#0B2240] placeholder:text-[#4C5A70]/50 focus:outline-none focus:ring-2 focus:ring-[#1FA3A3] transition-all ${
                  errors.last_name ? 'border-red-400' : 'border-gray-200'
                }`}
                data-testid="lead-form-last-name"
              />
              {errors.last_name && <p className="text-red-500 text-xs mt-1">{errors.last_name}</p>}
            </div>
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-[#0B2240] mb-1.5">
              Email Address <span className="text-red-500">*</span>
            </label>
            <input
              id="email"
              type="email"
              value={form.email}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="Email Address"
              className={`w-full px-4 py-3 rounded-xl border text-[16px] bg-[#F7F6F2] text-[#0B2240] placeholder:text-[#4C5A70]/50 focus:outline-none focus:ring-2 focus:ring-[#1FA3A3] transition-all ${
                errors.email ? 'border-red-400' : 'border-gray-200'
              }`}
              data-testid="lead-form-email"
            />
            {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
          </div>

          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-[#0B2240] mb-1.5">
              Phone Number (optional)
            </label>
            <input
              id="phone"
              type="tel"
              value={form.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              placeholder="Phone Number (optional)"
              className={`w-full px-4 py-3 rounded-xl border text-[16px] bg-[#F7F6F2] text-[#0B2240] placeholder:text-[#4C5A70]/50 focus:outline-none focus:ring-2 focus:ring-[#1FA3A3] transition-all ${
                errors.phone ? 'border-red-400' : 'border-gray-200'
              }`}
              data-testid="lead-form-phone"
            />
            {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
          </div>

          <div>
            <label htmlFor="business_name" className="block text-sm font-medium text-[#0B2240] mb-1.5">
              Business Name (if applicable)
            </label>
            <input
              id="business_name"
              type="text"
              value={form.business_name}
              onChange={(e) => handleChange('business_name', e.target.value)}
              placeholder="Business Name (if applicable)"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 text-[16px] bg-[#F7F6F2] text-[#0B2240] placeholder:text-[#4C5A70]/50 focus:outline-none focus:ring-2 focus:ring-[#1FA3A3] transition-all"
              data-testid="lead-form-business"
            />
          </div>

          <div>
            <label htmlFor="service_interest" className="block text-sm font-medium text-[#0B2240] mb-1.5">
              What can we help with? <span className="text-red-500">*</span>
            </label>
            <select
              id="service_interest"
              value={form.service_interest}
              onChange={(e) => handleChange('service_interest', e.target.value)}
              className={`w-full px-4 py-3 rounded-xl border text-[16px] bg-[#F7F6F2] text-[#0B2240] focus:outline-none focus:ring-2 focus:ring-[#1FA3A3] transition-all appearance-none ${
                errors.service_interest ? 'border-red-400' : 'border-gray-200'
              } ${!form.service_interest ? 'text-[#4C5A70]/50' : ''}`}
              data-testid="lead-form-service"
            >
              <option value="">Select a service...</option>
              {serviceOptions.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
            {errors.service_interest && <p className="text-red-500 text-xs mt-1">{errors.service_interest}</p>}
          </div>

          <div>
            <label htmlFor="notes" className="block text-sm font-medium text-[#0B2240] mb-1.5">
              Tell us a bit about your situation (optional)
            </label>
            <textarea
              id="notes"
              rows={3}
              value={form.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Tell us a bit about your situation (optional)"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 text-[16px] bg-[#F7F6F2] text-[#0B2240] placeholder:text-[#4C5A70]/50 focus:outline-none focus:ring-2 focus:ring-[#1FA3A3] transition-all resize-none"
              data-testid="lead-form-notes"
            />
          </div>

          {errors.submit && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-600" data-testid="lead-form-error">
              {errors.submit}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full py-4 rounded-xl text-[#0B2240] font-semibold text-base bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] shadow-[inset_0_1px_0_rgba(255,255,255,0.35),0_4px_20px_rgba(212,175,55,0.3)] hover:brightness-110 disabled:opacity-70 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center gap-2"
            data-testid="lead-form-submit"
          >
            {submitting ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                Schedule My Free Strategy Session
                <ArrowRight className="h-5 w-5" />
              </>
            )}
          </button>

          <p className="text-xs text-center text-[#4C5A70]">
            Your information is secure and will never be shared. We typically respond within one business day.
          </p>
        </form>
      </div>
    </section>
  );
};
